import React, {memo} from "react"
import {useTranslation} from "react-i18next";
import {DateTimePicker} from "@mui/x-date-pickers";
import {renderTimeViewClock} from '@mui/x-date-pickers/timeViewRenderers';


import Grid from "@mui/material/Grid";
import StationAutocomplete from "../../../components/inputs/StationsAutocomplete/StationAutocomplete";
import MDInput from "materialDashboard/components/MDInput";
import FormSection from "../../../components/FormSection";
import InputAdornment from "@mui/material/InputAdornment";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import {aspects} from "../../Stations/stationForm/useStationForm";
import {formikPropertyChanged} from "./index";
import EditLatLngMap from "../../../components/maps/EditLatLngMap";
import {useCurrentUser} from "../../../auth/CurrentUserProvider";
import MDButton from "../../../materialDashboard/components/MDButton";
import {preventValueChangeOnScroll} from "../../../utils/input";


const Metadata = memo(({formik, inputDisabled}) => {
    const {t} = useTranslation(['models23', 'enums']);
    const {currentUser} = useCurrentUser();

    const itinerantStation = formik.values.metadata.station && formik.values.metadata.station.type === "ITINERANT";

    const handlePositionChange = (position) => {
        formik.setFieldValue("metadata", {
            ...formik.values.metadata,
            lat: position.lat,
            lng: position.lng
        });
    }

    const setStationAndLocation = (station) => {
        if(!station){
            return;
        }
        const lat = station.geom.coordinates[1];
        const lng = station.geom.coordinates[0];
        const elevation = station.elevation ?? "";
        const slope = station.slope ?? "";
        const aspect = station.aspect ?? "";
        const locationName = station.locationName ?? "";

        formik.setFieldValue("metadata", {
            ...formik.values.metadata,
            station: station,
            lat: lat,
            lng: lng,
            elevation: elevation,
            slope: slope,
            aspect: aspect,
            locationName: locationName
        });
    }

    const handleStationSelect = (station) => {
        if(!station){
            formik.setFieldValue("metadata.station", null);
            return;
        }
        // Do not auto-fill coordinates for itinerant stations
        if(station.type === "ITINERANT"){
            formik.setFieldValue("metadata.station", station);
            return;
        }
        setStationAndLocation(station);
    }

    return <FormSection title={t('model23Form.metadataTitle')} error={Boolean(formik.errors.metadata) && formik.touched.metadata}>
        <Grid container rowSpacing={3} columnSpacing={4}>
            <Grid item xs={12} md={6}>
                <Grid container rowSpacing={3} columnSpacing={4}>
                    <Grid item xs={12}>
                        <DateTimePicker value={formik.values.metadata.datetime}
                                        label={t('model23Form.metadata.datetime')}
                                        onChange={(date) => formik.setFieldValue("metadata.datetime", date)}
                                        viewRenderers={{
                                            hours: renderTimeViewClock,
                                            minutes: renderTimeViewClock,
                                        }}
                                        sx={{width: "100%"}}
                                        slots={{
                                            // override default slots for better styling
                                            textField: MDInput
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                error: formik.touched.metadata?.datetime && Boolean(formik.errors.metadata?.datetime),
                                                helperText: (formik.touched.metadata?.datetime && formik.errors.metadata?.datetime)
                                            }
                                        }}
                                        ampm={false}
                                        disableFuture
                                        disabled={inputDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MDInput name="metadata.observers"
                                 label={t('model23Form.metadata.observers')}
                                 value={formik.values.metadata.observers ?? ""}
                                 onChange={formik.handleChange}
                                 error={formik.touched.metadata?.observers && Boolean(formik.errors.metadata?.observers)}
                                 helperText={formik.touched.metadata?.observers && formik.errors.metadata?.observers}
                                 variant="standard"
                                 fullWidth
                                 disabled={inputDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container columnSpacing={1} rowSpacing={4}>
                            <Grid item xs={12} md={itinerantStation ? 6 : 12}>
                                <StationAutocomplete name="metadata.stationId"
                                                     selectedStation={formik.values.metadata.station}
                                                     setSelectedStation={handleStationSelect}
                                                     stationFilters={{
                                                         accept_mod23: true,
                                                         acl__model23_write: true,
                                                         acl__user_id: currentUser.data.id
                                                     }}
                                                     fieldProps={{
                                                         error: formik.touched.metadata?.station && Boolean(formik.errors.metadata?.station),
                                                         helperText: formik.touched.metadata?.station && formik.errors.metadata?.station
                                                     }}
                                                     disabled={inputDisabled}/>
                            </Grid>
                            {
                                itinerantStation &&
                                <Grid item xs={12} md={6}>
                                    <MDButton variant="outlined"
                                              onClick={() => setStationAndLocation(formik.values.metadata.station)}
                                              disabled={inputDisabled}
                                              color="info"
                                              sx={{display: 'block', ml: 'auto'}}
                                    >
                                        {t('model23Form.metadata.useStationPositionData')}
                                    </MDButton>
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <MDInput name="metadata.locationName"
                                 label={t('model23Form.metadata.locationName')}
                                 value={formik.values.metadata.locationName ?? ""}
                                 onChange={formik.handleChange}
                                 error={formik.touched.metadata?.locationName && Boolean(formik.errors.metadata?.locationName)}
                                 helperText={formik.touched.metadata?.locationName && formik.errors.metadata?.locationName}
                                 variant="standard"
                                 fullWidth
                                 disabled={inputDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container columnSpacing={1}>
                            <Grid item xs={12} md={6}>
                                <MDInput name="metadata.lat"
                                         label={t('model23Form.metadata.lat')}
                                         value={formik.values.metadata.lat ?? ""}
                                         onChange={formik.handleChange}
                                         error={formik.touched.metadata?.lat && Boolean(formik.errors.metadata?.lat)}
                                         helperText={formik.touched.metadata?.lat && formik.errors.metadata?.lat}
                                         variant="standard"
                                         fullWidth
                                         InputProps={{
                                             endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                         }}
                                         type="number"
                                         onWheel={preventValueChangeOnScroll}
                                         disabled={inputDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MDInput name="metadata.lng"
                                         label={t('model23Form.metadata.lng')}
                                         value={formik.values.metadata.lng ?? ""}
                                         onChange={formik.handleChange}
                                         error={formik.touched.metadata?.lng && Boolean(formik.errors.metadata?.lng)}
                                         helperText={formik.touched.metadata?.lng && formik.errors.metadata?.lng}
                                         variant="standard"
                                         fullWidth
                                         InputProps={{
                                             endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                         }}
                                         type="number"
                                         onWheel={preventValueChangeOnScroll}
                                         disabled={inputDisabled}
                                />
                            </Grid>
                        </Grid>
                        <MDTypography variant="caption" fontWeight="regular" color="text" component="div" sx={{mt: 1}}>
                            (EPSG:4326 WGS 84) {t('model23Form.metadata.epsgioLink')} <a
                            href="https://epsg.io/transform#s_srs=23032&t_srs=4326" target="_blank"
                            rel="noreferrer">epsg.io</a>
                        </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDInput name="metadata.elevation"
                                 label={t('model23Form.metadata.elevation')}
                                 value={formik.values.metadata.elevation ?? ""}
                                 onChange={formik.handleChange}
                                 error={formik.touched.metadata?.elevation && Boolean(formik.errors.metadata?.elevation)}
                                 helperText={formik.touched.metadata?.elevation && formik.errors.metadata?.elevation}
                                 variant="standard"
                                 fullWidth
                                 type="number"
                                 InputProps={{
                                     endAdornment: <InputAdornment position="end">
                                         <Typography variant="caption">m</Typography>
                                     </InputAdornment>,
                                 }}
                                 disabled={inputDisabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDInput name="metadata.slope"
                                 label={t('model23Form.metadata.slope')}
                                 value={formik.values.metadata.slope ?? ""}
                                 onChange={formik.handleChange}
                                 error={formik.touched.metadata?.slope && Boolean(formik.errors.metadata?.slope)}
                                 helperText={formik.touched.metadata?.slope && formik.errors.metadata?.slope}
                                 variant="standard"
                                 fullWidth
                                 type="number"
                                 InputProps={{
                                     endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                 }}
                                 disabled={inputDisabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDInput name="metadata.aspect"
                                 label={t('model23Form.metadata.aspect')}
                                 value={formik.values.metadata.aspect}
                                 onChange={formik.handleChange}
                                 error={formik.touched.metadata?.aspect && Boolean(formik.errors.metadata?.aspect)}
                                 helperText={formik.touched.metadata?.aspect && formik.errors.metadata?.aspect}
                                 variant="standard"
                                 fullWidth
                                 select
                                 disabled={inputDisabled}>
                            <MenuItem value="">/</MenuItem>
                            {
                                aspects.map(option => (
                                    <MenuItem key={option} value={option}>
                                        {t('enums:aspect.' + option)}
                                    </MenuItem>
                                ))
                            }
                        </MDInput>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
                <EditLatLngMap lat={formik.values.metadata.lat}
                               lng={formik.values.metadata.lng}
                               onChange={handlePositionChange}
                               style={{height: "400px"}}
                               disabled={inputDisabled}/>
            </Grid>
        </Grid>
</FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'metadata'));

export default Metadata;